import { useState } from 'react';

import ProductItem from '@lcsc-ui/components/ProductItem/productItem';
import ProductList from '@lcsc-ui/components/ProductList';

import ActivityTab from '../components/ActivityTab';
import ProductDetails from '../components/ProductDetails';
import ProductTabs from '../components/ProductTabs';
import SectionNavigation from '../components/SectionNavigation';
import TechnicalDocsTable from '../components/TechnicalDocsTable';
import XinpinLayout from '../components/XinpinLayout';

import { getProductConfig } from './config';

const XinpinProductPage = ({
  productType,
  productRecordList = [],
  productRecordList2 = [],
  wwwTemplateResource = '',
  lcscItemUrl = '',
}) => {
  const [activeTab, setActiveTab] = useState(0);

  // 根据产品类型获取配置
  const rawConfig = getProductConfig(productType);

  // 递归处理配置，为所有 url/downloadUrl 添加 wwwTemplateResource 前缀
  const processConfig = (obj) => {
    if (!obj) return obj;
    if (typeof obj === 'string') return obj;
    if (Array.isArray(obj)) return obj.map(processConfig);

    const processed = {};
    for (const key in obj) {
      const value = obj[key];
      if (
        (key === 'url' || key === 'downloadUrl') &&
        typeof value === 'string' &&
        !value.startsWith('http') &&
        !value.startsWith('#')
      ) {
        processed[key] = `${wwwTemplateResource}/${value}`;
      } else if (typeof value === 'object') {
        processed[key] = processConfig(value);
      } else {
        processed[key] = value;
      }
    }
    return processed;
  };

  const config = wwwTemplateResource ? processConfig(rawConfig) : rawConfig;

  // 如果 rightContent 是函数，调用它并传入 wwwTemplateResource
  const rightContent =
    typeof config.rightContent === 'function' ? config.rightContent(wwwTemplateResource) : config.rightContent;

  // Handle activity link click
  const handleActivityClick = () => {
    setActiveTab(5); // Switch to activity tab
    setTimeout(() => {
      window.scrollTo({ top: 600, behavior: 'smooth' });
    }, 100);
  };

  return (
    <XinpinLayout breadcrumb={config.breadcrumb}>
      <ProductDetails
        productInfo={config.productInfo}
        onActivityClick={handleActivityClick}
        rightContent={rightContent}
      />

      <ProductTabs tabs={config.tabs} activeTab={activeTab} onTabChange={setActiveTab}>
        <XinpinTabContent
          activeTab={activeTab}
          productType={productType}
          productRecordList={productRecordList}
          productRecordList2={productRecordList2}
          config={config}
          lcscItemUrl={lcscItemUrl}
          wwwTemplateResource={wwwTemplateResource}
        />
      </ProductTabs>
    </XinpinLayout>
  );
};

// 通用标签页内容组件
const XinpinTabContent = ({
  activeTab,
  productType,
  productRecordList,
  productRecordList2,
  config,
  lcscItemUrl,
  wwwTemplateResource,
}) => {
  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // 功能与参数
        return (
          <FeaturesTab
            productType={productType}
            productRecordList={productRecordList}
            config={config}
            lcscItemUrl={lcscItemUrl}
          />
        );
      case 1: // 技术文档
        return <DocsTab config={config} productType={productType} />;
      case 2: // 工具与软件
        return <ToolsTab config={config} productType={productType} />;
      case 3: // 立即订购
        return <OrderTab productList={productRecordList2?.length > 0 ? productRecordList2 : []} />;
      case 4: // 支持与培训
        return <SupportTab config={config} productType={productType} wwwTemplateResource={wwwTemplateResource} />;
      case 5: // 活动相关
        return <ActivityTab config={config} productType={productType} />;
      default:
        return <FeaturesTab productType={productType} productRecordList={productRecordList} config={config} />;
    }
  };

  return <div className="pl-[30px] pr-[30px]">{renderTabContent()}</div>;
};

// 功能与参数标签页
const FeaturesTab = ({ productType, productRecordList, config, lcscItemUrl }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [modalImage, setModalImage] = useState(null);

  const navigationItems = [
    { label: '描述', targetId: '#describe', spm: 'ms' }, // 描述
    { label: '特性', targetId: '#characteristics', spm: 'tx' }, // 特性
    { label: '方框图', targetId: '#blockDiagram', spm: 'fkt' }, // 方框图
    { label: '适合您的设计资源', targetId: '#natural_resources', spm: 'zy' }, // 资源
    { label: '参数', targetId: '#parameters', spm: 'cs' }, // 参数
    { label: '您设计所需要的产品', targetId: '#products', spm: 'cp' }, // 产品
  ];

  return (
    <div className="block">
      <SectionNavigation items={navigationItems} />

      {/* description_details_wrap 模块 - 参考 HTML 结构 */}
      <div className={`pr-[59px] overflow-hidden transition-all duration-300 ${isExpanded ? 'h-auto' : 'h-[310px]'}`}>
        <div className="flex">
          {/* 描述 - description_details */}
          <div className="float-left" id="describe">
            <h3 className="text-base font-bold">描述</h3>
            <div className="w-[426px] mt-5">
              {config.features.description.map((paragraph, index) => (
                <p
                  key={index}
                  className={`leading-[22px] text-xs ${
                    index === config.features.description.length - 1 ? 'mt-[30px]' : ''
                  }`}
                >
                  {paragraph}
                </p>
              ))}
            </div>
          </div>

          {/* 特性 - description_details2 */}
          <div className="mx-[128px] float-left" id="characteristics">
            <h3 className="text-base font-bold">特性</h3>
            <div className="w-[300px]">
              {config.features.characteristics.map((section, sectionIndex) => (
                <ul key={sectionIndex} className="mt-[21px]">
                  <li className="ml-5 leading-6 relative">
                    <i className="absolute left-[-16px] top-1/2 -mt-1 w-2 h-2 bg-[url('//static.szlcsc.com/ecp/assets/web/page/www/xinpinGD32E231/images/index_icon_2.png')] bg-[0_0] bg-no-repeat"></i>
                    {section.title}
                  </li>
                  {section.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="ml-[33px] leading-6 relative">
                      <i className="block absolute top-[10px] -mt-[3px] left-[-12px] w-[6px] h-[6px] bg-[url('//static.szlcsc.com/ecp/assets/web/page/www/xinpinGD32E231/images/index_icon_2.png')] bg-[-22px_0] bg-no-repeat"></i>
                      {item}
                    </li>
                  ))}
                </ul>
              ))}
            </div>
          </div>

          {/* 方框图 - description_details3 */}
          <div className="float-right relative" id="blockDiagram">
            <h3 className="text-base font-bold">方框图</h3>
            <div>
              <div
                className="relative cursor-pointer"
                onClick={() =>
                  setModalImage({
                    src: config.features.blockDiagram.image,
                    alt: config.features.blockDiagram.alt,
                    title: '方框图',
                  })
                }
              >
                <div className="w-[60px] h-[60px] bg-black/70 rounded-full absolute left-1/2 -ml-[30px] top-1/2 -mt-[30px] hover:bg-black/80 transition-colors">
                  <div className="w-7 h-[30px] block my-3 mx-auto bg-[url('//static.szlcsc.com/ecp/assets/web/page/www/xinpinGD32E231/images/index_icon_2.png')] bg-[-48px_3px] bg-no-repeat cursor-pointer"></div>
                </div>
                <img
                  src={config.features.blockDiagram.image}
                  alt={config.features.blockDiagram.alt}
                  className="mt-[21px] w-[180px] h-[207px]"
                />
              </div>
              <p className="text-center mt-[15px]">{config.features.blockDiagram.caption}</p>
            </div>
          </div>

          <div className="clear-both"></div>
        </div>
      </div>

      {/* 查看更多内容按钮 - more_content_wrap */}
      <div className="mt-6 mb-8">
        <div className="h-px mt-6 relative border-b border-dashed border-[#e4e4e4]">
          <p
            className="bg-white w-[88px] absolute left-1/2 -ml-11 -top-2 cursor-pointer hover:text-[#0093e6] transition-colors"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            <span className="block w-full text-center text-[#0093e6]">{isExpanded ? '收起内容' : '查看更多内容'}</span>
          </p>
        </div>
      </div>

      {/* 适合您的设计资源 */}
      <div className="mt-8" id="natural_resources">
        <h3 className="text-lg font-bold mb-5 text-gray-800">适合您的设计资源</h3>

        {/* 技术文档 */}
        {config.designResources?.technicalDocs && config.designResources.technicalDocs.length > 0 && (
          <div className="mb-8">
            <h4 className="text-base font-semibold mb-4 text-gray-700">技术文档</h4>
            <TechnicalDocsTable
              technicalDocs={{ technicalDocs: config.designResources.technicalDocs }}
              showNavigation={false}
              mode="simple"
              productType={productType}
            />
          </div>
        )}

        {/* 设计与开发工具 */}
        {config.designResources?.developmentTools && config.designResources.developmentTools.length > 0 && (
          <div className="mb-8">
            <h4 className="text-base font-semibold mb-4 text-gray-700">设计与开发工具</h4>
            <TechnicalDocsTable
              technicalDocs={{ developmentTools: config.designResources.developmentTools }}
              showNavigation={false}
              mode="simple"
              productType={productType}
            />
          </div>
        )}

        {/* 参考设计 */}
        {config.designResources?.referenceDesigns && config.designResources.referenceDesigns.length > 0 && (
          <div className="mb-8">
            <h4 className="text-base font-semibold mb-4 text-gray-700">参考设计</h4>
            <ul className="space-y-0" data-spm="cksj">
              {config.designResources.referenceDesigns.map((design, index) => (
                <li
                  key={index}
                  className="w-[358px] px-[10px] pb-[10px] pt-[22px] border border-solid border-[#ebebeb] border-t-2 border-t-[#0094e7]"
                >
                  <div className="cursor-default mb-4">
                    <img
                      src={design.image}
                      alt={design.title || '参考设计图片'}
                      className="w-full max-w-none h-auto"
                      onError={(e) => {
                        e.target.src =
                          'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDYwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI2MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yNTAgMjAwSDM1MFYzMDBIMjUwVjIwMFoiIGZpbGw9IiNEMUQ1REIiLz4KPHN2Zz4K';
                      }}
                    />
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed mb-4">{design.description}</p>
                  <a
                    data-spm={`xq.${index + 1}`}
                    href={design.url}
                    download
                    className="inline-block text-[#0093e6] text-sm font-medium transition-colors duration-200 hover:underline"
                  >
                    了解详情{'>>'}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* 参数表格 (仅GD32E231显示) */}
      {config.features.parametersTable && (
        <div className="mt-8" id="parameters">
          <h3 className="text-lg font-bold mb-5 text-gray-800">参数</h3>
          <div className="overflow-x-auto border-t-2 border-solid border-[#0094e7]">
            <table
              className="w-full border-collapse bg-white rounded-lg overflow-hidden shadow-md"
              style={{ tableLayout: 'fixed' }}
            >
              <thead>
                <tr>
                  {config.features.parametersTable.headers.map((header, index) => {
                    // 为特定列设置宽度
                    const style = {};
                    if (index === 0)
                      style.width = '140px'; // Part number
                    else if (index === 1)
                      style.width = '100px'; // 立即下单
                    else if (index === 2)
                      style.width = '120px'; // cpu
                    else if (index === 15)
                      style.width = '200px'; // Operating temperature range (C)
                    else if (index === 17)
                      style.width = '220px'; // Analog-to-digital converter (ADC)
                    else style.width = '80px'; // 其他列默认宽度

                    return (
                      <th
                        key={index}
                        className="px-2 py-3 text-center border-b-2 border-solid border-[#e0e0e0] text-xs text-[#666666] bg-gray-50"
                        style={style}
                      >
                        {header}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {config.features.parametersTable.rows.map((row, index) => (
                  <tr
                    key={index}
                    className="border-b-1 border-solid border-[#e0e0e0] hover:bg-gray-50 transition-colors"
                  >
                    {row.map((cell, cellIndex) => {
                      // 为特定列设置宽度
                      const style = {};
                      if (cellIndex === 0)
                        style.width = '140px'; // Part number
                      else if (cellIndex === 1)
                        style.width = '100px'; // 立即下单
                      else if (cellIndex === 2)
                        style.width = '120px'; // cpu
                      else if (cellIndex === 15)
                        style.width = '200px'; // Operating temperature range (C)
                      else if (cellIndex === 17)
                        style.width = '220px'; // Analog-to-digital converter (ADC)
                      else style.width = '80px'; // 其他列默认宽度

                      return (
                        <td
                          key={cellIndex}
                          className={`px-2 py-3 text-center text-xs ${
                            cellIndex === 0 ? 'font-medium text-gray-800' : 'text-gray-600'
                          }`}
                          style={style}
                        >
                          {cellIndex === 1 && cell.includes('立即下单') ? (
                            <a
                              data-spm={`dd.${index + 1}`}
                              href={`${lcscItemUrl}/${cell.split('|')[1]}.html`}
                              className="bg-blue-500 hover:bg-blue-400 text-white px-3 py-1 rounded text-xs inline-block transition-all hover:-translate-y-0.5 hover:shadow-md"
                            >
                              立即下单
                            </a>
                          ) : (
                            cell
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 产品列表 - 您设计所需要的产品 */}
      {productRecordList && productRecordList.length > 0 ? (
        <div className="mt-8" id="products">
          <h3 className="text-lg font-bold mb-5 text-gray-800">您设计所需要的产品</h3>
          <ProductList
            searchResult={{
              productRecordList: productRecordList,
              currePage: 1,
            }}
          >
            {productRecordList.map((product, index) => (
              <div key={product.productVO?.productId || index} className="mb-[10px]">
                <ProductItem
                  productVO={product.productVO}
                  index={index}
                  spm={`design${index}`}
                  showGroupProduct={product.isGroupProduct}
                  isGroupProduct={product.isGroupProduct}
                  showSecondKill={product.isSecondKill}
                  isSecondKill={product.isSecondKill}
                  moreChannelVO={product.moreChannelVO}
                />
              </div>
            ))}
          </ProductList>
        </div>
      ) : null}

      {/* 图片弹窗 */}
      {modalImage && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setModalImage(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-[600px] w-full max-h-[92vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-bold">{modalImage.title || '图片预览'}</h3>
              <span
                className="cursor-pointer text-2xl text-gray-500 hover:text-gray-700 leading-none"
                onClick={() => setModalImage(null)}
              >
                ×
              </span>
            </div>
            <div className="p-6 flex items-center justify-center">
              <img
                src="//static.szlcsc.com/ecp/assets/web/common/images/block_scheme.png"
                width="600px"
                header="606px"
                alt=""
                className="h-auto"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// 其他标签页组件 (简化版本)
const DocsTab = ({ config, productType }) => (
  <TechnicalDocsTable technicalDocs={config.technicalDocs} productType={productType} />
);

const ToolsTab = ({ config, productType }) => (
  <TechnicalDocsTable technicalDocs={config.toolsAndSoftware} productType={productType} />
);

const OrderTab = ({ productList }) => {
  return (
    <div className="block">
      {productList && productList.length > 0 ? (
        <ProductList
          searchResult={{
            productRecordList: productList,
            currePage: 1,
          }}
        >
          {productList.map((product, index) => (
            <div key={product.productVO?.productId || index} className="mb-[10px]">
              <ProductItem
                productVO={product.productVO}
                index={index}
                spm={`order${index}`}
                showGroupProduct={product.isGroupProduct}
                isGroupProduct={product.isGroupProduct}
                showSecondKill={product.isSecondKill}
                isSecondKill={product.isSecondKill}
                moreChannelVO={product.moreChannelVO}
              />
            </div>
          ))}
        </ProductList>
      ) : (
        <div className="py-10 px-5 text-center bg-gray-50 rounded-lg my-5">
          <h3 className="text-gray-600 mb-2">立即订购</h3>
          <p className="text-gray-400 text-sm">暂无可订购产品</p>
        </div>
      )}
    </div>
  );
};

const SupportTab = ({ config, productType, wwwTemplateResource = '' }) => {
  const navigationItems = [
    { label: '支持与培训', targetId: '#support', spm: 'zc' }, // 支持
    { label: '视频', targetId: '#videos', spm: 'sp' }, // 视频
  ];

  // 根据产品类型渲染不同的视频内容
  const renderVideoContent = () => {
    if (productType === 'GD32E231') {
      return (
        <div className="bg-white overflow-hidden">
          <div className="flex flex-row">
            {/* 左侧视频区域 */}
            <div className="mr-[15px]">
              <h3 className="text-base font-bold">视频</h3>
              <div className="relative overflow-hidden">
                <video
                  controls
                  preload="auto"
                  poster="//static.szlcsc.com/ecp/assets/web/static/images/video1.jpg"
                  className="block"
                  style={{ width: '517px', height: '296px' }}
                >
                  <source src={`${wwwTemplateResource}/GD32E231%20START.mp4`} type="video/mp4" />
                  <source src={`${wwwTemplateResource}/GD32E231%20START.webm`} type="video/webm" />
                  <source src={`${wwwTemplateResource}/GD32E231%20START.ogv`} type="video/ogg" />
                  <p className="text-white p-4">您的浏览器版本过低请更换最新版本！</p>
                </video>
              </div>
            </div>

            {/* 右侧描述区域 */}
            <div className="w-1/2 pt-[40px]">
              <h3 className="text-base font-bold mb-[15px] text-gray-800">GD32E231开发板简介</h3>
              <div className="space-y-4">
                <p className="text-xs text-gray-700 leading-relaxed">
                  GD32E231基于Cortex-M23内核，采用了业界领先的55nm低功耗工艺制程，主频高达72MHz，配备了16KB到64KB的嵌入式闪存及4KB到8KB的SRAM，配合内置的硬件乘法器、除法器和加速单元。GD32E231不仅拥有超值的高速处理能力，片上集成了多达5个16位通用定时器、1个16位基本定时器和1个多通道DMA控制器。通用接口则包括2个USART、2个SPI、2个I2C、1个I2S。另外，还提供了1个支持三相脉宽调制PWM输出和霍尔采集接口的16位高级定时器，1个高速轨到轨输入/输出模拟电压比较器，1个12位2.6MSPS采样率的高性能ADC支持多通道高速数据采集。GD32E231全面适用于工业自动化、电机控制、LED显示、家用电器及电子玩具、智慧城市与智能家居、电子支付、电动车、飞行器、机器人等多种应用场合。
                </p>

                {/* 视频信息 */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <p className="text-xs text-gray-500 mb-1">发表：2019-4-16</p>
                  <p className="text-xs text-gray-500">视频时长：2分5秒</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (productType === 'XILINXPYNQZ2') {
      return (
        <div className="overflow-hidden">
          {/* 图文标题 */}
          <h3 className="text-base font-bold mb-2.5">图文</h3>

          {/* 视频和描述容器 */}
          <div className="flex">
            {/* 左侧图片区域 */}
            <div className="w-[506px] mr-[15px]">
              <img
                src="//static.szlcsc.com/ecp/assets/web/page/www/xinpinXILINXPYNQZ2/images/xinpintuwenpxun.png"
                width="506px"
                alt="PYNQ-Z2 Setup Guide"
              />
            </div>

            {/* 右侧描述区域 */}
            <div className="flex-1 pt-3">
              <h3 className="text-base font-bold my-[15px]">PYNQ-Z2 Setup Guide</h3>
              <p className="text-sm leading-[1.9]">
                <b>Prerequisites</b>
                <br />
                • PYNQ-Z2 board
                <br />
                • Computer with compatible browser (Supported Browsers)
                <br />
                • Ethernet cable
                <br />
                • Micro USB cable
                <br />
                • Micro-SD card with preloaded image, or blank card (Minimum 8GB recommended)Getting Started
                <br />
                You can watch the getting started video guide, or follow the instructions in Board Setup.
                <br />
              </p>
            </div>
          </div>

          {/* Board Setup 详细说明 */}
          <div className="mt-5 text-sm leading-[1.9]">
            <b>Board Setup</b>
            <br />
            1. Set the ** Boot** jumper to the SD position. (This sets the board to boot from the Micro-SD card)
            <br />
            2. To power the board from the micro USB cable, set the Power jumper to the USB position. (You can also
            power the board from an external 12V power regulator by setting the jumper to REG.)
            <br />
            3. Insert the Micro SD card loaded with the PYNQ-Z2 image into the Micro SD card slot underneath the board
            <br />
            4. Connect the USB cable to your PC/Laptop, and to the PROG - UART MicroUSB port on the board
            <br />
            5. Connect the Ethernet port by following the instructions below
            <br />
            6. Turn on the PYNQ-Z2 and check the boot sequence by following the instructions below
            <br />
            <b>Turning On the PYNQ-Z2</b>
            <br />
            As indicated in step 6 of Board Setup, slide the power switch to the ON position to turn on the board. The
            Red LED will come on immediately to confirm that the board has power. After a few seconds, the Yellow/Green
            / Done LED will light up to show that the Zynq® device is operational.
            <br />
            After a minute you should see two Blue ** LEDs and four **Yellow/Green LEDs flash simultaneously. The Blue
            LEDs will then turn on and off while the Yellow/Green LEDs remain on. The system is now booted and ready for
            use.
            <br />
            Once your board is setup, you need to connect to it to start using Jupyter notebook.
            <br />
            If available, you should connect your board to a network or router with Internet access. This will allow you
            to update your board and easily install new packages.
            <br />
            You will need to have an Ethernet port available on your computer, and you will need to have permissions to
            configure your network interface. With a direct connection, you will be able to use PYNQ, but unless you can
            bridge the Ethernet connection to the board to an Internet connection on your computer, your board will not
            have Internet access. You will be unable to update or load new packages without Internet access.
            <br />
            Connect directly to a computer (Static IP):
            <br />
            1. Assign your computer a static IP address
            <br />
            2. Connect the board to your computer&apos;s Ethernet port
            <br />
            3. Browse to http://192.168.2.99
            <br />
            If you connect to a router, or a network with a DHCP server, your board will automatically get an IP
            address. You must make sure you have permission to connect a device to your network, otherwise the board may
            not connect properly.
            <br />
            Connect to a Router/Network (DHCP):
            <br />
            1. Connect the Ethernet port on your board to a router/switch
            <br />
            2. Connect your computer to Ethernet or WiFi on the router/switch
            <br />
            3. Browse to http://&lt;board IP address&gt;
            <br />
            4. Optional: Change the Hostname
            <br />
            5. Optional: Configure Proxy Settings
            <br />
          </div>
        </div>
      );
    }

    // 默认内容
    return (
      <div className="py-10 px-5 text-center bg-gray-50 rounded-lg">
        <h3 className="text-gray-600 mb-2">视频内容</h3>
        <p className="text-gray-400 text-sm">视频内容将在后续版本中完善</p>
      </div>
    );
  };

  return (
    <div className="block">
      <SectionNavigation items={navigationItems} />

      {/* 支持与培训内容 - 每个产品可以有不同的布局 */}
      <div id="support" className="mb-8">
        <h2 className="text-base mb-[10px] font-bold">支持与培训</h2>

        <div className="mb-6">
          <a
            rel="nofollow"
            href="http://club.szlcsc.com/article/details_39613_1.html"
            className="inline-flex items-center justify-center text-[#FFFFFF] rounded-[4px] bg-[#0093E6] w-[266px] h-[32px] hover:opacity-[0.6] transition-opacity"
            data-spm="jszc"
          >
            从立创电子工程师交流社区搜索答案
          </a>
        </div>

        <div className="mb-8">
          <p className="text-gray-600 text-sm leading-relaxed">
            我们的工程师团队在各个领域都有专业知识，可以帮助您解决技术问题，自己开发解决方案，并且提供相关技术支持。不管是立创商城的元器件问题，
          </p>
        </div>
      </div>

      {/* 视频内容 - 根据产品类型显示不同内容 */}
      <div id="videos" className="mb-8">
        {renderVideoContent()}
      </div>
    </div>
  );
};

export default XinpinProductPage;
